import { useNavigate } from 'react-router-dom'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { Button } from '@factorialco/factorial-one-react'
import {
  Alert,
  AlertDescription,
  DotTag,
  RawTag,
  StatusTag,
} from '@factorialco/factorial-one-react/dist/experimental'
import { ChevronRight } from '@factorialco/factorial-one-react/icons/app'
import { AtsCandidateApplicationsListItemFragment } from 'generated/resources/graphql'
import { capitalize } from 'lodash'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import { phaseColor } from 'modules/ats/components/ApplicationPhaseTag'
import { timeAgoWhenRecent } from 'modules/ats/lib/dates'

interface Props {
  relatedApplications: AtsCandidateApplicationsListItemFragment[]
  totalApplicationsCount: number
  currentApplicationId?: number
  title?: string
}

export const CandidateApplicationsList = ({
  totalApplicationsCount,
  relatedApplications,
  currentApplicationId,
  title,
}: Props) => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()

  const showPermissionsWarning = totalApplicationsCount > relatedApplications.length

  if (totalApplicationsCount === 0) {
    return null
  }

  const statusLabel: Record<string, string> = {
    draft: i18n.t('ats.job_status.draft'),
    published: i18n.t('ats.job_status.active'),
    archived: i18n.t('ats.job_status.archived'),
    unlisted: i18n.t('ats.job_status.unlisted'),
    cancelled: i18n.t('ats.job_status.cancelled'),
  }

  return (
    <Box gap="s16">
      <Box background="grey000White" flexDirection="row" alignItems="center" gap="s8" flexGrow>
        <Text.Normal size="200" color="textSecondary" weight="medium">
          {title || i18n.t('ats.candidate_all_applications.title')}
        </Text.Normal>
        ·
        <RawTag text={relatedApplications.length.toString()} />
      </Box>
      <Box gap="s16">
        {showPermissionsWarning && (
          <Alert variant="warning">
            <AlertDescription>
              {i18n.t('ats.candidate_all_applications.permissions_warning', {
                count: totalApplicationsCount,
              })}
            </AlertDescription>
          </Alert>
        )}
        {totalApplicationsCount > 0 && (
          <Box
            borderRadius={{ all: 'abs016' }}
            width="full"
            border={{
              all: {
                width: 's1',
                style: 'solid',
                color: 'grey400',
              },
            }}
          >
            {relatedApplications.map((application, index) => {
              const { id, relatedJobPosting, qualified, applicationPhase, rejectionReason } =
                application
              const appliedDate = capitalize(timeAgoWhenRecent(application.createdAt))
              const isCurrentApplication = currentApplicationId === id
              const isLastItem = relatedApplications.length - 1 === index

              return (
                <Box
                  key={`application=${application.id}`}
                  flexDirection="row"
                  justifyContent="spaceBetween"
                  alignItems="center"
                  gap="s4"
                  width="full"
                  paddingY="s12"
                  paddingX="s12"
                  border={
                    isLastItem
                      ? undefined
                      : {
                          bottom: {
                            width: 's1',
                            style: 'solid',
                            color: 'grey400',
                          },
                        }
                  }
                >
                  <Box gap="s2">
                    <Text.Normal size="200" color="textPrimary" weight="medium">
                      {relatedJobPosting.title}
                    </Text.Normal>
                    <Box flexDirection="row" gap="s4">
                      <Text.Normal size="200">
                        {[relatedJobPosting.location?.name, appliedDate]
                          .filter(Boolean)
                          .join(' - ')}
                      </Text.Normal>
                      ·
                      <Text.Normal size="200" color="grey700">
                        {statusLabel[relatedJobPosting.status]}
                      </Text.Normal>
                    </Box>
                  </Box>
                  <Box flexDirection="row" gap="s6">
                    {qualified ? (
                      <DotTag
                        text={applicationPhase.name}
                        color={phaseColor(applicationPhase.hiringStage?.name || 'default')}
                      />
                    ) : (
                      <StatusTag
                        text={
                          !!rejectionReason?.reason
                            ? rejectionReason.reason
                            : i18n.t('ats.application_details.rejected_status')
                        }
                        variant="critical"
                      />
                    )}
                    {!isCurrentApplication && !!relatedJobPosting._policies?.manage.can && (
                      <Button
                        label={i18n.t(
                          'ats.candidate_all_applications.navigate_to_job_application_label',
                          {
                            job_posting_title: relatedJobPosting.title,
                          }
                        )}
                        icon={ChevronRight}
                        variant="outline"
                        onClick={() =>
                          navigate(
                            navigation.ats.jobs.jobsId.applications.applicationDetail.fullPath({
                              jobId: relatedJobPosting.id,
                              application_id: id,
                            })
                          )
                        }
                        size="sm"
                        hideLabel
                        round
                      />
                    )}
                  </Box>
                </Box>
              )
            })}
          </Box>
        )}
      </Box>
    </Box>
  )
}
